import { type ReactNode } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Breadcrumb, type BreadcrumbItem } from '../../ui';

// Shared page header (DESIGN_LANGUAGE_SPEC §15 staggered fadeInUp, §24 page layout). Provides
// the consistent hierarchy used across the tenancy views: breadcrumb trail, a large title with
// scale contrast, a muted description, and an optional action slot on the right.

export interface PageHeaderProps {
  title: string;
  description?: string | undefined;
  breadcrumbs?: BreadcrumbItem[] | undefined;
  actions?: ReactNode | undefined;
}

export function PageHeader({ title, description, breadcrumbs, actions }: PageHeaderProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <header
      className="mb-8"
      style={{ animation: 'fadeInUp 0.6s ease-out forwards' }}
    >
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb items={breadcrumbs} className="mb-3" />
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1
            className={`text-3xl font-bold tracking-tight ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            {title}
          </h1>
          {description && (
            <p className={`mt-2 text-sm ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
              {description}
            </p>
          )}
        </div>
        {actions && <div className="flex flex-shrink-0 items-center gap-3">{actions}</div>}
      </div>
    </header>
  );
}
